/* eslint-disable react/no-multi-comp */
/* eslint-disable react/prop-types */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Button,
    Table,
    ConfigProvider,
    Tag,
    Tooltip,
    type TableColumnsType,
} from '@app/ui';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import * as format from '@app/utils/formatters';
import { getIcon } from '@app/utils/getSocialIcons';

export interface DownloadPostDataType {
    id: string;
    cdnMediaUrl: string;
    createdAt: string;
    socialPlatform: string;
    username: string;
    sponsorAnnotationsDetails: any[];
}

interface RowContextProps {
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
        <Button
            type="text"
            size="large"
            icon={
                <HolderOutlined
                    style={{
                        color: '#529EBD',
                        width: '20px',
                        fontWeight: 'bold',
                    }}
                    rotate={90}
                />
            }
            style={{ cursor: 'move' }}
            ref={setActivatorNodeRef}
            {...listeners}
        />
    );
};

const getColumns = (
    t: (key: string) => string,
): TableColumnsType<DownloadPostDataType> => [
    {
        title: t('socialMediaExposure.table.media'),
        dataIndex: 'cdnMediaUrl',
        align: 'center',
        render: (url: string) => (
            <div
                style={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    margin: '0 auto',
                }}
            >
                <img
                    src={url}
                    alt="media"
                    style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                    }}
                />
            </div>
        ),
    },
    {
        title: t('socialMediaExposure.table.date'),
        dataIndex: 'createdAt',
        align: 'center',
        render: (date: string) => <span>{format.shortDate(date)}</span>,
    },
    {
        title: t('socialMediaExposure.table.platform'),
        dataIndex: 'socialPlatform',
        align: 'center',
        render: (platform: string) => {
            const lowerSocName = platform.toLowerCase();
            return <Tooltip title={platform}>{getIcon(lowerSocName)}</Tooltip>;
        },
    },
    {
        title: t('socialMediaExposure.table.account'),
        dataIndex: 'username',
        align: 'center',
    },
    {
        title: t('socialMediaExposure.table.placementSponsor'),
        dataIndex: 'sponsorAnnotationsDetails',
        align: 'center',
        render: (details: any[]) => {
            if (!Array.isArray(details) || details.length === 0) return null;

            return (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 4,
                        justifyContent: 'center',
                    }}
                >
                    {details.map((item, index) => {
                        const placementName = item?.placement?.name || '—';
                        const sponsorName = item?.sponsor?.name || '—';
                        const label = `${placementName} - ${sponsorName}`;

                        return (
                            <Tooltip title={label} key={index} zIndex={9999}>
                                <Tag
                                    style={{
                                        maxWidth: 130,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {label}
                                </Tag>
                            </Tooltip>
                        );
                    })}
                </div>
            );
        },
    },
    {
        key: 'sort',
        align: 'center',
        width: 60,
        render: () => <DragHandle />,
    },
];

const Row: React.FC<
    React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key': string }
> = props => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props['data-row-key'] });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    const contextValue = useMemo<RowContextProps>(
        () => ({ setActivatorNodeRef, listeners }),
        [setActivatorNodeRef, listeners],
    );

    return (
        <RowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} {...attributes} />
        </RowContext.Provider>
    );
};

interface DownloadPostDragTableProps {
    posts: DownloadPostDataType[];
    setPosts: React.Dispatch<React.SetStateAction<DownloadPostDataType[]>>;
}

const DownloadPostDragTable: React.FC<DownloadPostDragTableProps> = ({
    posts,
    setPosts,
}) => {
    const { t } = useTranslation('measurement');

    const columns = useMemo(() => getColumns(t), [t]);

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setPosts(prevState => {
                const activeIndex = prevState.findIndex(
                    p => p.id === active.id,
                );
                const overIndex = prevState.findIndex(p => p.id === over?.id);
                return arrayMove(prevState, activeIndex, overIndex);
            });
        }
    };

    return (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext
                items={posts.map(p => p.id)}
                strategy={verticalListSortingStrategy}
            >
                <ConfigProvider
                    theme={{
                        token: {
                            fontSize: 12,
                            fontFamily: 'pnregular',
                        },
                        components: {
                            Table: {
                                cellFontSizeSM: 12,
                                headerBg: '#e1effa',
                                borderColor: '#0000000F',
                            },
                        },
                    }}
                >
                    <Table<DownloadPostDataType>
                        className="table-v2"
                        rowKey="id"
                        components={{ body: { row: Row } }}
                        columns={columns}
                        dataSource={posts}
                        pagination={false}
                        scroll={{ y: 400 }}
                        bordered
                        size="small"
                        rowHoverable={false}
                    />
                </ConfigProvider>
            </SortableContext>
        </DndContext>
    );
};

export default DownloadPostDragTable;
